import { Component } from "react";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import DropdownDatePicker from "@shared/v2/DropdownDatePicker";
import TimePicker from "@shared/v2/DateSelector/TimePicker";
import { VALID_DATE_FORMATS } from "@shared/v2/DateSelector/utils/constants";
import * as css from "./person-detail-styles.module.css";
import { updatePersonPromise } from "../actions/updatePerson";
import DatePicker from "../../shared/v2/DatePicker";
import IconButton from "../../shared/v2/IconButton";
import { PencilSolidV6, XmarkSolidV6 } from "../../shared/v2/Icomoon";
import Button from "../../shared/v2/Button";
import PersonDetailError from "./PersonDetailError";

const PICKER_DATE_FORMAT = "MM/DD/YYYY";
const API_DATE_FORMAT = "YYYY-MM-DD";
const YEARLESS_PLACEHOLDER_YEAR = 2000; // a leap year
// Stored/API time format (e.g. "4:15 PM") vs. TimePicker option format (e.g. "04:15 PM")
const API_TIME_FORMAT = "h:mm A";
const PICKER_TIME_FORMAT = "hh:mm A";

// The v2 DatePicker only accepts specific string formats, so always feed it MM/DD/YYYY
const toPickerDate = (value) => {
  if (!value) return "";
  // Yearless dates ("0000-MM-DD") parse as 1900 (not a leap year), turning 02/29 into 03/01.
  // Swap in a leap year so the month/day survive.
  const [year, month, day] = String(value).split("-");
  const parsed = dayjs(Number(year) < 100 ? `${YEARLESS_PLACEHOLDER_YEAR}-${month}-${day}` : value);
  return parsed.isValid() ? parsed.format(PICKER_DATE_FORMAT) : "";
};

// The v2 DatePicker re-emits valid typed input as-is, so accept every format it considers valid
const toApiDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value, [PICKER_DATE_FORMAT, ...VALID_DATE_FORMATS], true);
  return parsed.isValid() ? parsed.format(API_DATE_FORMAT) : null;
};

const toPickerTime = (value) => {
  if (!value) return "";
  const parsed = dayjs(value, [API_TIME_FORMAT, PICKER_TIME_FORMAT], true);
  return parsed.isValid() ? parsed.format(PICKER_TIME_FORMAT) : "";
};

const toApiTime = (value) => {
  const parsed = dayjs(value, [PICKER_TIME_FORMAT, API_TIME_FORMAT], true);
  return parsed.isValid() ? parsed.format(API_TIME_FORMAT) : value;
};

const hasTimeWithoutDate = (customDate) =>
  customDate?.include_time &&
  customDate?.time_of_day_string &&
  (!customDate.date || !dayjs(customDate.date).isValid());

class EditableDate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editingDate: false,
      editablePerson: JSON.parse(JSON.stringify(props.person)),
      errors: null,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { person } = this.props;
    if (person !== nextProps.person) {
      this.setState({ editablePerson: JSON.parse(JSON.stringify(nextProps.person)) });
    }
  }

  dateName = () => {
    const { date, elementType } = this.props;
    const name = elementType === "anniversary" ? "home anniversary" : elementType;
    return date.name?.split("_").join(" ") || name;
  };

  onEditDate = () => {
    this.setState({ editingDate: true });
  };

  onDismissDateEdit = () => {
    const { person } = this.props;
    this.setState({
      editingDate: false,
      editablePerson: JSON.parse(JSON.stringify(person)),
      errors: null,
    });
  };

  handleCustomDateChange = (dateId, newDateOrTime, type) => {
    const { editablePerson } = this.state;
    const { custom_dates: customDates } = editablePerson.data.attributes;
    const indexOfDate = customDates.indexOf(customDates.find((date) => date.id === dateId));

    if (type === "date") {
      customDates[indexOfDate].date = newDateOrTime;
    } else {
      customDates[indexOfDate].time_of_day_string = newDateOrTime;
    }

    this.setState({
      editablePerson: {
        ...editablePerson,
        data: {
          ...editablePerson.data,
          attributes: {
            ...editablePerson.data.attributes,
            custom_dates: customDates,
          },
        },
      },
    });
  };

  handleDateChange = (property, value) => {
    const { editablePerson } = this.state;
    this.setState({
      editablePerson: {
        ...editablePerson,
        data: {
          ...editablePerson.data,
          attributes: {
            ...editablePerson.data.attributes,
            [property]: {
              ...editablePerson.data.attributes[property],
              date: value,
            },
          },
        },
      },
    });
  };

  onDateSave = () => {
    const { elementType, updatePersonData, date: propDate } = this.props;
    const { editablePerson } = this.state;
    const { birthdate, custom_dates: customDates = [] } = editablePerson.data.attributes;

    let errors = null;

    if (elementType === "birthdate" && birthdate?.date) {
      const [year, month, day] = birthdate.date.split("-");
      if (!month) errors = ["Cannot save without a month"];
      if (!day) errors = ["Cannot save without a day"];
      if (year && !month && !day) errors = ["Cannot save without a month and day"];
    }

    if (elementType === "custom_date") {
      const customDate = customDates.find((date) => date.id === propDate.id);
      if (hasTimeWithoutDate(customDate)) {
        errors = ["Cannot save without a date"];
      }
    }

    this.setState({ errors });
    if (errors) return;

    updatePersonPromise(editablePerson)
      .then((r) => {
        updatePersonData(r.data);
        this.setState({ editingDate: false, errors: null });
      })
      .catch((err) => {
        this.setState({ errors: err.response.data.errors });
      });
  };

  renderDate = () => {
    const { date } = this.props;
    // Yearless dates are stored as "0000-MM-DD". dayjs maps year 0 to 1900 (not a leap year),
    // which would turn 02/29 into 03/01, so take month/day straight from the string.
    const [year, month, day] = String(date.date).split("-");
    const formattedDate =
      Number(year) < 1900 && month && day ? `${month}/${day}` : dayjs(date.date).format(PICKER_DATE_FORMAT);
    if (date.include_time) {
      return `${formattedDate} at ${date.time_of_day_string}`;
    }
    return formattedDate;
  };

  renderAlert = () => {
    const { errors } = this.state;
    // eslint-disable-next-line react/no-array-index-key
    return errors.map((error, i) => <PersonDetailError key={i} error={error} />);
  };

  renderButtons = () => (
    <div className="tw-flex tw-justify-end tw-gap-[8px]">
      <Button onClick={this.onDateSave}>Save</Button>
      <IconButton size="small" onClick={this.onDismissDateEdit}>
        <XmarkSolidV6 />
      </IconButton>
    </div>
  );

  editDateFields = () => {
    const { elementType } = this.props;
    return elementType === "custom_date" ? this.renderCustomDateFields() : this.renderDateFields();
  };

  renderCustomDateFields = () => {
    const { date: propDate, noYear } = this.props;
    const { editablePerson, errors } = this.state;
    const { custom_dates: customDates } = editablePerson.data.attributes;
    const indexOfDate = customDates.indexOf(customDates.find((date) => date.id === propDate.id));
    const customDate = customDates[indexOfDate];
    const date = toPickerDate(customDate.date);

    return (
      <div className="tw-flex tw-flex-col tw-gap-[8px]">
        {errors && this.renderAlert()}
        <div className="tw-flex tw-justify-between tw-gap-[8px]">
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[4px]">
            <DatePicker
              inputProps={{ size: 10 }}
              name={customDate.name}
              value={date}
              onChange={(value) => this.handleCustomDateChange(customDate.id, toApiDate(value), "date")}
              noYear={noYear}
            />
            {customDate.include_time && (
              <TimePicker
                time={toPickerTime(customDate.time_of_day_string)}
                placeholder="Select time"
                handleTimeChange={(value) => this.handleCustomDateChange(customDate.id, toApiTime(value), "time")}
              />
            )}
          </div>
          {this.renderButtons()}
        </div>
      </div>
    );
  };

  renderDateFields = () => {
    const { elementType, noYear } = this.props;
    const {
      editablePerson: {
        data: { attributes },
      },
      errors,
    } = this.state;
    const date = toPickerDate(attributes[elementType].date);

    return (
      <div className="tw-flex tw-gap-[8px] tw-justify-between">
        <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[4px]">
          {errors && this.renderAlert()}
          {elementType === "birthdate" && (
            <>
              <DropdownDatePicker
                value={attributes[elementType].date}
                onChange={(d) => this.handleDateChange(elementType, d)}
              />
              {this.renderButtons()}
            </>
          )}
          {elementType !== "birthdate" && (
            <DatePicker
              inputProps={{ size: 10 }}
              dateFormat={PICKER_DATE_FORMAT}
              name={elementType}
              value={date}
              onChange={(value) => this.handleDateChange(elementType, toApiDate(value))}
              noYear={noYear}
            />
          )}
        </div>
        {elementType !== "birthdate" && this.renderButtons()}
      </div>
    );
  };

  render() {
    const { editingDate } = this.state;
    return (
      <div className="tw-flex tw-justify-between tw-gap-[8px] hover:tw-bg-gray-5 tw-group tw-py-[8px] tw-px-[20px]">
        <div className="tw-flex tw-flex-col tw-w-full">
          <div className={css.brivityPersonDetailsLabel}>{this.dateName()}</div>
          {editingDate ? this.editDateFields() : this.renderDate()}
        </div>
        {!editingDate && (
          <div className="tw-invisible group-hover:tw-visible">
            <IconButton size="small" onClick={this.onEditDate}>
              <PencilSolidV6 />
            </IconButton>
          </div>
        )}
      </div>
    );
  }
}

EditableDate.propTypes = {
  date: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    date: PropTypes.string,
    include_time: PropTypes.bool,
    time_of_day_string: PropTypes.string,
  }).isRequired,
  elementType: PropTypes.string.isRequired,
  noYear: PropTypes.bool,
  person: PropTypes.shape().isRequired,
  updatePersonData: PropTypes.func.isRequired,
};

EditableDate.defaultProps = {
  noYear: false,
};

export default EditableDate;
